import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import { useRouteParams } from "@/hooks/useEncodedIds";
import Confetti from "react-confetti";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "../../components/Button";
import Layout from "../../components/Layout";
import { Spinner } from "../../components/Spinner";
import useLivlyUser from "@/context/UserProvider";
import moment from "moment-timezone";
import { getFormattedTime } from "../../utils/bookingHelpers";
import { BaseLivlyApiResponse } from "../../types/Base";
import { BASE_API_URL } from "../../utils/constants";

async function getAppDownloadUrl(leaseId: string) {
  const result = await axios.get<BaseLivlyApiResponse<{ downloadUrl: string }>>(
    `${BASE_API_URL}/resident/me/leases/${leaseId}/app-download-url`
  );
  return result.data.Data;
}

export default function BookingConfirmedPage() {
  const params = useRouteParams<{ leaseId: string }>();
  const { user } = useLivlyUser();
  const location = useLocation();
  const [state] = useState(
    () =>
      location.state as {
        amenityName?: string;
        startTime?: string;
        endTime?: string;
        isConditionalAccessEnabled?: boolean;
      } | null
  );

  const [dismissed, setDismissed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  const amenityName = state?.amenityName;
  const startTime = state?.startTime;
  const endTime = state?.endTime;
  const isConditionalAccessEnabled = state?.isConditionalAccessEnabled ?? false;

  const { data: appDownloadData, isLoading: isAppLinkLoading } = useQuery({
    queryKey: ["app-download-url", params.leaseId],
    queryFn: () => getAppDownloadUrl(params.leaseId),
    enabled: isConditionalAccessEnabled
  });

  const appLink = appDownloadData?.downloadUrl ?? "";

  const { startEnd } = getFormattedTime(startTime ?? null, endTime ?? null);
  const dateDisplay = startTime
    ? `${moment(startTime).format("ddd, MMM D")}, ${startEnd}`
    : "";

  return (
    <Layout title="Booking Confirmed">
      {showConfetti && !isAppLinkLoading && (
        <Confetti
          recycle={false}
          numberOfPieces={200}
          onConfettiComplete={() => setShowConfetti(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 50,
            pointerEvents: "none"
          }}
        />
      )}
      <div>
        <p className="mb-6 text-gray-700">
          {amenityName}
          {dateDisplay ? ` \u2014 ${dateDisplay}` : ""}
        </p>

        {isConditionalAccessEnabled && !dismissed && (
          <div className="p-4 mb-6 bg-gray-50 border-l-4 border-blue-500 rounded-lg">
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5 text-blue-500">
                <FontAwesomeIcon icon="key" size="lg" />
              </div>
              <div className="flex-1">
                <p className="m-0 font-semibold">Unlock with the Livly app</p>
                <p className="m-0 mt-1 text-sm text-gray-500">
                  This amenity requires the Livly app to unlock the door during
                  your booking window.
                </p>
                <div className="flex gap-3 mt-3">
                  {isAppLinkLoading ? (
                    <Spinner color="livly" />
                  ) : (
                    <>
                      <a
                        href={appLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src="/apple_badge.png"
                          alt="download in apple store"
                          className="h-10"
                        />
                      </a>
                      <a
                        href={appLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src="/google_badge.png"
                          alt="download in google play store"
                          className="h-10"
                        />
                      </a>
                    </>
                  )}
                </div>
                <button
                  className="mt-3 text-sm text-gray-500 hover:text-gray-700"
                  onClick={() => setDismissed(true)}
                >
                  Dismiss &times;
                </button>
              </div>
            </div>
          </div>
        )}

        <Link to={`/lease/${params.leaseId}/bookings`}>
          <Button size="small" color="secondary" className="w-full md:w-auto">
            View My Bookings
          </Button>
        </Link>
      </div>
    </Layout>
  );
}
